import { Attribute, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { TranslocoModule, translate } from '@jsverse/transloco';
import { getFormErrorMessage } from '@app/core/utils/form-error-message.util';

@Component({
  standalone: true,
  selector: 'ups-password',
  templateUrl: './form-password.component.html',
  styleUrls: ['./form-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
  imports: [
    IonicModule,
    TranslocoModule
  ],
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: FormPasswordComponent
    },
    {
      multi: true,
      provide: NG_VALIDATORS,
      useExisting: FormPasswordComponent
    }
  ]
})
export class FormPasswordComponent implements ControlValueAccessor, Validator {

  @Input() public mustSame: boolean = false;
  @Input() public showStrength: boolean = false;
  @Input() public label: string = translate('form.password');
  @Input() public required: boolean = false;
  public readonly passwordDesc: string[] = [ '', translate('validation.password.weak'), translate('validation.password.medium'), translate('validation.password.strong'), translate('validation.password.very_strong') ];
  public type: string = 'password';
  public passStrength: number = 0;
  public errorMsg: string = '';
  public value: string = '';
  public formControl!: AbstractControl;
  public isTouched: boolean = false;
  protected onChange: (_val: any) => void = (_val: any) => {};
  protected onTouched: () => void = () => {};

  constructor(
    @Attribute('autocomplete') public autocomplete: 'current-password' | 'new-password' = 'current-password',
    protected readonly ch: ChangeDetectorRef
  ) {}

  public registerOnChange(fn: any): void{ this.onChange = fn; }
  
  public registerOnTouched(fn: any): void{ this.onTouched = fn; } 

  public changeType(): void{
    this.type = this.type === 'password'? 'text' : 'password';
  }
  
  public writeValue(val: any): void{ 
    this.value = val; 
    this.ch.markForCheck();
  }

  public input({ value }: any): void{
    if (!this.isTouched) {
      this.onTouched();
      this.isTouched = true;
    }

    this.value = value;
    this.onChange(this.value);
    this.passwordCheck();
  }

  public validate(control: AbstractControl): ValidationErrors | null{
    this.formControl = control;
    return null;
  }

  public passwordCheck(): void{
    this.errorMsg = '';
    if (this.showStrength === true) {
      this.passStrength = 0;
      
      this.checkPasswordStrength(/(?=.*[0-9])/, 'number');
      this.checkPasswordStrength(/(?=.*[A-Z])/, 'capital');
      this.checkPasswordStrength(/(?=.*[!%,&@#$^*?_~<>])/, 'special');
      if (this.formControl!.value.length >= 8) this.passStrength++;
    }

    this.passwordControlInvalid();
  }

  public passwordControlInvalid(): void{
    if (this.passStrength === 4) this.errorMsg = '';
    else {
      if (!this.formControl!.touched) return;

      if (this.mustSame === true && this.formControl.parent?.hasError('notmatched') === true) {
        this.errorMsg = translate('validation.must_same', { field: this.label, field2: translate('form.password') });
        return;
      }
      this.errorMsg = getFormErrorMessage(this.formControl.errors, this.label);
    }
  }

  protected checkPasswordStrength(regex: RegExp, message: string): void{
    if (regex.test(this.formControl!.value)) this.passStrength++;
    else this.errorMsg = translate(`validation.must_${ message }`, { field: this.label, count: '1' });
  }
}
